/**
 * The 400ms/3s loading contract (DESIGN.md § State Patterns, EXPERIENCE.md
 * § State Patterns, "speed is a feature made concrete"): nothing renders
 * before 400ms; a skeleton shaped like the content renders between 400ms
 * and 3s; after 3s, one added line says loading is taking unusually long.
 *
 * `computeLoadingPhase` is the decision by itself, deliberately separated
 * from the React hook that drives it with a real timer
 * (`useLoadingPhase`, same file) so the *decision* is testable as a pure
 * function — no timers, no DOM, no React tree — while the hook stays a thin
 * wrapper nothing else needs to reason about. This split is also what makes
 * VC-2.2-17 true by construction: the phase is a function of `isPending`
 * alone, never `isFetching` — a background refetch of already-visible data
 * (`isFetching && !isPending`) cannot produce a loading phase because the
 * function never receives `isFetching` in the first place.
 */

export const SKELETON_DELAY_MS = 400;
export const UNUSUAL_DELAY_MS = 3000;

export type LoadingPhase = "none" | "skeleton" | "unusual";

/**
 * `isPending` is TanStack Query's own flag for "no data has ever resolved
 * for this query yet" (v5: `status === "pending"`). With
 * `placeholderData: keepPreviousData` (every list query this story adds),
 * changing sort/filter/page keeps the previous page's rows on screen and
 * flips `isPending` back to `false` immediately — only the very first load
 * of a screen is ever `isPending`, which is exactly the case DESIGN.md's
 * contract describes ("cold load").
 */
export function computeLoadingPhase(isPending: boolean, elapsedMs: number): LoadingPhase {
  if (!isPending) {
    return "none";
  }
  if (elapsedMs < SKELETON_DELAY_MS) {
    return "none";
  }
  if (elapsedMs < UNUSUAL_DELAY_MS) {
    return "skeleton";
  }
  return "unusual";
}
